import React, { useState } from 'react';
import { Order, OrderStatus } from '../types';
import { StatusBadge } from './StatusBadge';

const NEXT_STATUS: Record<OrderStatus, OrderStatus | null> = {
  NEW: 'PREPARING',
  PREPARING: 'READY',
  READY: 'COMPLETED',
  COMPLETED: null,
};

const NEXT_STATUS_LABEL: Record<OrderStatus, string> = {
  NEW: 'Start Preparing',
  PREPARING: 'Mark Ready',
  READY: 'Complete Order',
  COMPLETED: '',
};

interface OrderCardProps {
  order: Order;
  onStatusChange: (orderId: string, status: OrderStatus) => Promise<void>;
  isNew?: boolean;
}

export const OrderCard: React.FC<OrderCardProps> = ({ order, onStatusChange, isNew }) => {
  const [updating, setUpdating] = useState(false);

  const elapsedMinutes = Math.floor(
    (Date.now() - new Date(order.createdAt).getTime()) / 60000
  );

  const nextStatus = NEXT_STATUS[order.status];
  const nextLabel = NEXT_STATUS_LABEL[order.status];

  const handleAdvance = async () => {
    if (!nextStatus) return;
    setUpdating(true);
    try {
      await onStatusChange(order.orderId, nextStatus);
    } finally {
      setUpdating(false);
    }
  };

  const isUrgent = elapsedMinutes >= 10 && order.status !== 'COMPLETED';
  const shortId = order.orderId.slice(-8).toUpperCase();

  return (
    <div
      className={`relative flex flex-col bg-surface-700/80 border rounded-2xl overflow-hidden transition-all duration-300 animate-slide-up hover:scale-[1.01] hover:shadow-2xl ${
        isNew ? 'ring-2 ring-brand-500/60 shadow-brand-500/20 shadow-xl' : ''
      } ${
        isUrgent && order.status !== 'COMPLETED'
          ? 'border-red-500/40'
          : 'border-white/8'
      }`}
    >
      {/* Status top bar */}
      <div
        className={`h-1 w-full ${
          order.status === 'NEW' ? 'bg-orange-500' :
          order.status === 'PREPARING' ? 'bg-yellow-400' :
          order.status === 'READY' ? 'bg-emerald-400' :
          'bg-gray-600'
        }`}
      />

      <div className="p-5">
        {/* Card Header */}
        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-white font-black text-xl">#{shortId}</span>
              {isNew && (
                <span className="text-xs bg-brand-500 text-white px-2 py-0.5 rounded-full font-bold animate-pulse">
                  NEW
                </span>
              )}
            </div>
            <p className="text-gray-400 text-xs">
              Kiosk: <span className="text-gray-300">{order.kioskId}</span>
              {order.customerName && (
                <> · <span className="text-gray-300">{order.customerName}</span></>
              )}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <StatusBadge status={order.status} />
            <span
              className={`text-xs font-medium ${
                isUrgent ? 'text-red-400' : 'text-gray-500'
              }`}
            >
              ⏱ {elapsedMinutes}m ago
            </span>
          </div>
        </div>

        {/* Items List */}
        <div className="space-y-2 mb-4">
          {order.items.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/8 text-white font-bold text-sm">
                  {item.quantity}×
                </span>
                <span className="text-gray-200 text-sm font-medium">{item.name}</span>
              </div>
              <span className="text-gray-400 text-sm">${item.subtotal.toFixed(2)}</span>
            </div>
          ))}
        </div>

        {/* Notes */}
        {order.notes && (
          <div className="mb-4 px-3 py-2 rounded-lg bg-yellow-400/10 border border-yellow-400/20">
            <p className="text-yellow-300 text-xs font-medium">📝 {order.notes}</p>
          </div>
        )}

        {/* Totals */}
        <div className="flex items-center justify-between pt-3 border-t border-white/8">
          <div className="text-xs text-gray-500 space-y-0.5">
            <p>Sub: ${order.subtotal.toFixed(2)}</p>
            <p>Tax: ${order.tax.toFixed(2)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total</p>
            <p className="text-white font-black text-xl">${order.total.toFixed(2)}</p>
          </div>
        </div>

        {/* Payment Method */}
        <div className="mt-2 flex items-center gap-1.5">
          <span className="text-xs text-gray-500">💵</span>
          <span className="text-xs text-gray-500 font-medium">
            {order.paymentMethod.replace('_', ' ')}
          </span>
        </div>
      </div>

      {/* Action Button */}
      {nextStatus && (
        <div className="px-5 pb-5">
          <button
            onClick={handleAdvance}
            disabled={updating}
            className={`w-full py-3 rounded-xl font-bold text-sm transition-all duration-200 disabled:opacity-50 ${
              order.status === 'NEW'
                ? 'bg-orange-500 hover:bg-orange-400 text-white shadow-orange-500/30'
                : order.status === 'PREPARING'
                ? 'bg-emerald-500 hover:bg-emerald-400 text-white shadow-emerald-500/30'
                : 'bg-gray-600 hover:bg-gray-500 text-white'
            } shadow-lg`}
          >
            {updating ? '⏳ Updating...' : nextLabel}
          </button>
        </div>
      )}

      {order.status === 'COMPLETED' && (
        <div className="px-5 pb-4">
          <div className="w-full py-2.5 rounded-xl text-center text-sm font-semibold text-gray-600 bg-white/4">
            ✓ Order Complete
          </div>
        </div>
      )}
    </div>
  );
};
